import React from 'react';
import { Sparkles } from 'lucide-react';
import LoadingDots from './LoadingDots';
import { ModalCloseButton } from './ModalCloseButton';

interface InterpretationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  priceLabel: string;
  isProcessing?: boolean;
}

export const InterpretationModal: React.FC<InterpretationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  priceLabel,
  isProcessing = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="absolute inset-0" onClick={onClose} aria-hidden />

      <div className="relative bg-gradient-to-b from-slate-900 to-purple-950 rounded-2xl max-w-md w-full mx-4 shadow-2xl border border-purple-500/20 overflow-hidden min-h-[520px]">
        <ModalCloseButton onClose={onClose} className="text-white/80" />

        <div className="px-6 py-8 space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-200" />
              <h2 className="crypto-heading text-white text-xl">{title}</h2>
            </div>
            <p className="text-white/70 text-sm leading-relaxed">
              Use a touch of magic (and a dash of AI) to get a quick interpretation that fits your spread.
            </p>
          </div>

          <div className="bg-black/20 rounded-xl border border-white/10 px-5 py-5 space-y-3 text-sm text-white/80">
            <div className="flex items-center space-x-2">
              <Sparkles className="w-4 h-4 text-purple-200" />
              <span>Highlights the key energy in your spread.</span>
            </div>
            <div className="flex items-center space-x-2">
              <Sparkles className="w-4 h-4 text-purple-200" />
              <span>Instant delivery straight inside Stradamus.</span>
            </div>
            <div className="flex items-center space-x-2">
              <Sparkles className="w-4 h-4 text-purple-200" />
              <span>Incorporates the question you asked to personalize the insight.</span>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={onConfirm}
              disabled={isProcessing}
              className={`w-full py-3 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 transition-colors text-white font-semibold text-sm tracking-wide flex items-center justify-center gap-3 ${isProcessing ? 'opacity-60 pointer-events-none' : ''}`}
            >
              {isProcessing ? (
                <>
                  <LoadingDots className="text-purple-50" />
                  <span>Summoning insight…</span>
                </>
              ) : (
                `Unlock for ${priceLabel}`
              )}
            </button>
            <p className="text-[13px] text-white/50 text-center">
              You&apos;ll approve a Base transaction inside your Warpcast wallet. Once it lands on-chain, the interpretation unlocks automatically.
            </p>
            <button
              onClick={onClose}
              disabled={isProcessing}
              className={`w-full py-2 text-sm text-white/60 hover:text-white transition-colors ${isProcessing ? 'opacity-50 pointer-events-none' : ''}`}
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
